/**
 * Search Overlay – Uses Shopify's native predictive search
 * Simple: single API call + language filtering for multi-market stores
 */
(() => {
  'use strict';

  const OVERLAY_SELECTOR = '[data-search-overlay]';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_DELAY = 250;
  const PRODUCTS_LIMIT = 50;

  const FOCUSABLE_SELECTOR = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"])';

  const ESCAPE_LOOKUP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  const CART_ICON = '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M20.7269 24.0784H7.28688C6.04928 24.0784 5.04688 23.076 5.04688 21.8384V10.0784C5.04688 9.15108 5.79952 8.39844 6.72688 8.39844H21.2869C22.2142 8.39844 22.9669 9.15108 22.9669 10.0784V21.8384C22.9669 23.076 21.9645 24.0784 20.7269 24.0784Z" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.4808 12.0847C18.3262 12.0847 18.2008 12.2101 18.2019 12.3647C18.2019 12.5192 18.3273 12.6447 18.4819 12.6447C18.6364 12.6447 18.7619 12.5192 18.7619 12.3647C18.7619 12.2101 18.6364 12.0847 18.4808 12.0847" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.52763 12.0847C9.37307 12.0847 9.24763 12.2101 9.24875 12.3647C9.24875 12.5192 9.37419 12.6447 9.52875 12.6447C9.68331 12.6447 9.80875 12.5192 9.80875 12.3647C9.80875 12.2101 9.68331 12.0847 9.52763 12.0847" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.5312 8.39406V8.11406V8.11406C9.5312 5.79454 11.4117 3.91406 13.7312 3.91406H14.2912C16.6108 3.91406 18.4912 5.79454 18.4912 8.11406V8.11406V8.39406" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (c) => ESCAPE_LOOKUP[c] || c);

  const formatMoney = (cents) => {
    const value = parseInt(cents, 10);
    if (isNaN(value)) return '';
    if (window.Shopify?.formatMoney) {
      try { return window.Shopify.formatMoney(value); } catch (e) { /* fallback */ }
    }
    return (value / 100).toFixed(2).replace('.', ',') + ' zł';
  };

  const getUtils = () => window.AylaTheme?.searchUtils || null;

  const getTranslations = () => {
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    if (!overlay) return { addToCart: 'Dodaj do koszyka', unavailable: 'W Produkcji' };
    try {
      const json = overlay.getAttribute('data-translations');
      if (json) {
        const t = JSON.parse(json);
        return { addToCart: t.add_to_cart || 'Dodaj do koszyka', unavailable: t.unavailable || 'W Produkcji' };
      }
    } catch (e) { /* ignore */ }
    return { addToCart: 'Dodaj do koszyka', unavailable: 'W Produkcji' };
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Product Card Markup
  // ─────────────────────────────────────────────────────────────────────────────

  const buildProductCard = (product, config) => {
    if (!product) return '';

    const utils = getUtils();
    const title = escapeHtml(product.title || '');
    const rawUrl = product.url || `/products/${product.handle || ''}`;
    const url = escapeHtml(utils?.makeMarketAwareUrl(rawUrl) || rawUrl);
    const imageSrc = product.featured_image?.url || product.image || config.placeholderImage || '';
    const imageUrl = escapeHtml(imageSrc);
    
    // Handle price - API returns price as string like "31.90", convert to cents
    const priceValue = product.price ? Math.round(parseFloat(product.price) * 100) : 0;
    const price = formatMoney(priceValue);
    
    const isAvailable = product.available !== false;
    const translations = getTranslations();

    // Check if we have variant data for quick-add
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((v) => v?.available) || variants[0];
    const hasVariant = variant?.id;

    // If we have a variant, show add-to-cart form; otherwise just link to product
    const actionContent = hasVariant ? `
      <form class="search-result-form" action="/cart/add" method="post">
        <input type="hidden" name="id" value="${escapeHtml(variant.id)}">
        <button type="submit" class="search-result-add"${isAvailable ? '' : ' disabled aria-disabled="true"'}>
          <span class="add-to-cart-button">
            ${CART_ICON}
            <span class="add-to-cart-text">${escapeHtml(isAvailable ? translations.addToCart : translations.unavailable)}</span>
          </span>
          <span class="search-result-price">${price}</span>
        </button>
      </form>
    ` : `
      <a href="${url}" class="search-result-add search-result-link">
        <span class="add-to-cart-button">
          ${CART_ICON}
          <span class="add-to-cart-text">${escapeHtml(isAvailable ? translations.addToCart : translations.unavailable)}</span>
        </span>
        <span class="search-result-price">${price}</span>
      </a>
    `;

    return `
      <article class="search-result-card" data-search-result>
        <a class="search-result-image" href="${url}">
          <img src="${imageUrl}" alt="${title}" loading="lazy" width="236" height="236">
        </a>
        <div class="search-result-info">
          <a class="search-result-title" href="${url}">${title}</a>
          ${actionContent}
        </div>
      </article>
    `;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Main
  // ─────────────────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', () => {
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    if (!overlay) return;

    const utils = getUtils();
    const shouldHideProduct = utils?.shouldHideProductForCurrentLanguage || (() => false);

    const toggles = document.querySelectorAll('[data-search-toggle]');
    const mobileMenuOverlay = document.querySelector('[data-mobile-menu-overlay]');
    const background = overlay.querySelector('[data-search-background]');
    const closeButton = overlay.querySelector('[data-search-close]');
    const clearButton = overlay.querySelector('[data-search-clear]');
    const input = overlay.querySelector('[data-search-input]');
    const resultsGrid = overlay.querySelector('[data-search-results]');
    const resultsContainer = overlay.querySelector('[data-search-panel]');
    const emptyState = overlay.querySelector('[data-search-empty]');
    const loadingState = overlay.querySelector('[data-search-loading]');
    const errorState = overlay.querySelector('[data-search-error]');
    const emptyMessage = emptyState?.querySelector('p');
    const defaultEmptyMessage = emptyMessage?.textContent || '';

    const config = {
      placeholderImage: overlay.getAttribute('data-placeholder-image') || ''
    };

    const body = document.body;

    let debounceTimer = null;
    let activeController = null;

    // ─────────────────────────────────────────────────────────────────────────────
    // State
    // ─────────────────────────────────────────────────────────────────────────────

    const setState = (state) => {
      if (resultsGrid) resultsGrid.hidden = state !== 'results';
      if (loadingState) loadingState.hidden = state !== 'loading';
      if (emptyState) emptyState.hidden = state !== 'empty';
      if (errorState) errorState.hidden = state !== 'error';
    };

    const showEmpty = (message) => {
      if (emptyMessage) emptyMessage.textContent = message || defaultEmptyMessage;
      if (resultsGrid) resultsGrid.innerHTML = '';
      setState('empty');
    };

    const showError = () => {
      if (resultsGrid) resultsGrid.innerHTML = '';
      setState('error');
    };

    const renderResults = (products, query) => {
      if (!products?.length) {
        showEmpty(`Brak wyników dla "${query}".`);
        return;
      }

      const html = products.map((p) => buildProductCard(p, config)).filter(Boolean).join('');
      if (!html) {
        showEmpty(`Brak wyników dla "${query}".`);
        return;
      }

      resultsGrid.innerHTML = html;
      if (resultsContainer) resultsContainer.scrollTop = 0;
      setState('results');
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Search API
    // ─────────────────────────────────────────────────────────────────────────────

    // Get the base path for the search endpoint (handles locale prefixes like /pl-pl/)
    const getLocalePrefix = () => utils?.getLocalePrefix?.();
    
    const getPredictiveEndpoint = () => {
      const localePrefix = getLocalePrefix();
      return localePrefix ? `/${localePrefix}/search/suggest.json` : '/search/suggest.json';
    };
    
    const getSearchEndpoint = () => {
      const localePrefix = getLocalePrefix();
      return localePrefix ? `/${localePrefix}/search` : '/search';
    };

    // Polish word forms - map search terms to all forms that should match
    // Includes dual-purpose tags for products that are for both cats and dogs
    const DUAL_TAGS = ['for-both', 'for-all', 'both', 'all-pets', 'universal'];
    
    const POLISH_WORD_FORMS = {
      // Dog forms (includes dual-purpose tags)
      'pies': ['pies', 'psa', 'psu', 'psem', 'psie', 'psy', 'psów', 'psom', 'dog', 'for-dog', ...DUAL_TAGS],
      'psa': ['pies', 'psa', 'psu', 'psem', 'psie', 'psy', 'psów', 'psom', 'dog', 'for-dog', ...DUAL_TAGS],
      'psu': ['pies', 'psa', 'psu', 'psem', 'psie', 'psy', 'psów', 'psom', 'dog', 'for-dog', ...DUAL_TAGS],
      'dog': ['pies', 'psa', 'psu', 'psem', 'psie', 'psy', 'psów', 'psom', 'dog', 'for-dog', ...DUAL_TAGS],
      // Cat forms (includes dual-purpose tags)
      'kot': ['kot', 'kota', 'kotu', 'kotem', 'kocie', 'koty', 'kotów', 'cat', 'for-cat', ...DUAL_TAGS],
      'kota': ['kot', 'kota', 'kotu', 'kotem', 'kocie', 'koty', 'kotów', 'cat', 'for-cat', ...DUAL_TAGS],
      'cat': ['kot', 'kota', 'kotu', 'kotem', 'kocie', 'koty', 'kotów', 'cat', 'for-cat', ...DUAL_TAGS],
      // Duck forms
      'kaczka': ['kaczk', 'kacz', 'duck'],
      'kaczki': ['kaczk', 'kacz', 'duck'],
      'duck': ['kaczk', 'kacz', 'duck'],
      // Chicken forms
      'kurczak': ['kurczak', 'kurczaka', 'kurcz', 'chicken'],
      'chicken': ['kurczak', 'kurczaka', 'kurcz', 'chicken'],
      // Turkey forms  
      'indyk': ['indyk', 'indyka', 'turkey'],
      'turkey': ['indyk', 'indyka', 'turkey'],
    };

    // Get search terms - returns array of terms to match against
    const getSearchTerms = (query) => {
      const q = query.trim().toLowerCase();
      const words = q.split(/\s+/).filter(w => w.length > 0);
      const allTerms = new Set();
      
      // Process each word in the query
      words.forEach(word => {
        // Check if we have predefined forms for this word
        if (POLISH_WORD_FORMS[word]) {
          POLISH_WORD_FORMS[word].forEach(term => allTerms.add(term));
        } else {
          // For other words, use stemming
          let stem = word;
          if (word.length > 5) stem = word.slice(0, -2);
          else if (word.length > 4) stem = word.slice(0, -1);
          allTerms.add(stem);
          allTerms.add(word);
        }
      });
      
      // Also add the full query for exact phrase matching
      allTerms.add(q);
      
      return Array.from(allTerms);
    };

    // Build predictive search URL (max 10 results - Shopify limit)
    const buildPredictiveUrl = (query) => {
      const searchTerm = getSearchStem(query);
      const params = new URLSearchParams();
      params.set('q', searchTerm);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '10');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,vendor,tag,variants.title');
      params.set('resources[options][prefix]', 'last');
      return `${getPredictiveEndpoint()}?${params.toString()}`;
    };
    
    // Build regular search URL (can return more results)
    const buildSearchUrl = (query) => {
      const searchTerm = getSearchStem(query);
      const params = new URLSearchParams();
      params.set('q', searchTerm + '*');
      params.set('type', 'product');
      params.set('view', 'json');
      return `${getSearchEndpoint()}?${params.toString()}`;
    };

    // Sort by product line: Delight first, then Help, then Rescue
    const getProductLinePriority = (product) => {
      const tags = (product.tags || []).map(t => String(t).toLowerCase());
      const title = (product.title || '').toLowerCase();
      
      if (tags.includes('ayla-delight') || tags.includes('delight') || title.includes('delight')) {
        return 1;
      }
      if (tags.includes('ayla-help') || tags.includes('help') || title.includes('help')) {
        return 2;
      }
      if (tags.includes('ayla-rescue') || tags.includes('rescue') || title.includes('rescue')) {
        return 3;
      }
      return 4;
    };

    // Normalize product from /products.json format
    const normalizeStorefrontProduct = (product) => {
      if (!product) return null;
      return {
        title: product.title,
        handle: product.handle,
        url: `/products/${product.handle}`,
        price: product.variants?.[0]?.price || product.price,
        available: product.available,
        tags: product.tags || [],
        image: product.images?.[0]?.src || product.featured_image,
        featured_image: product.images?.[0] ? { url: product.images[0].src } : null,
        variants: product.variants || []
      };
    };

    // Client-side search through all products
    const searchProducts = async (query) => {
      const searchTerms = getSearchTerms(query);
      
      // Fetch all products from /products.json
      const localePrefix = getLocalePrefix();
      const productsUrl = localePrefix ? `/${localePrefix}/products.json?limit=250` : '/products.json?limit=250';
      
      console.log('[Search] Search terms:', searchTerms);
      console.log('[Search] Current language:', utils?.getCurrentLanguage?.());
      
      const response = await fetch(productsUrl, {
              method: 'GET',
        headers: { Accept: 'application/json' },
        signal: activeController.signal
      });
      
      if (!response.ok) throw new Error(`Products fetch failed: ${response.status}`);
      
      const data = await response.json();
      const allProducts = data?.products || [];
      
      // Filter products that match ANY search term AND are visible for current language
      const matchingProducts = allProducts.filter((product) => {
        // First check language visibility
        const normalizedProduct = normalizeStorefrontProduct(product);
        if (shouldHideProduct(normalizedProduct)) {
          return false;
        }
        
        // Then check search term match - product must match at least one term
        const title = (product.title || '').toLowerCase();
        const tags = (product.tags || []).map(t => String(t).toLowerCase()).join(' ');
        const handle = (product.handle || '').toLowerCase();
        const productType = (product.product_type || '').toLowerCase();
        
        const searchableText = `${title} ${tags} ${handle} ${productType}`;
        return searchTerms.some(term => searchableText.includes(term));
      });
      
      console.log('[Search] Matching products:', matchingProducts.length);
      
      return matchingProducts.map(normalizeStorefrontProduct).filter(Boolean);
    };

    const search = async (query) => {
      if (activeController) activeController.abort();
      activeController = new AbortController();

      setState('loading');

      try {
        // Use client-side search through all products
        const rawProducts = await searchProducts(query);
        
        // Sort by product line priority
        const sortedProducts = [...rawProducts].sort((a, b) => {
          return getProductLinePriority(a) - getProductLinePriority(b);
        });
        
        const products = sortedProducts.slice(0, PRODUCTS_LIMIT);

        renderResults(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Search error:', error);
        showError();
      }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Input Handler
    // ─────────────────────────────────────────────────────────────────────────────

    const handleInput = () => {
      if (!input) return;
      const query = input.value.trim();

      if (clearButton) clearButton.hidden = query.length === 0;

      if (query.length < MIN_QUERY_LENGTH) {
        showEmpty(defaultEmptyMessage);
          return;
      }

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => search(query), DEBOUNCE_DELAY);
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Overlay Controls
    // ─────────────────────────────────────────────────────────────────────────────

    const resetPanel = () => {
      clearTimeout(debounceTimer);
      if (activeController) { activeController.abort(); activeController = null; }
      if (input) input.value = '';
      if (clearButton) clearButton.hidden = true;
      if (resultsGrid) { resultsGrid.innerHTML = ''; resultsGrid.hidden = true; }
      showEmpty(defaultEmptyMessage);
    };

    const handleFocusTrap = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(overlay.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
        return !el.hasAttribute('hidden') && el.offsetParent !== null;
      });
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const openOverlay = (trigger) => {
      overlay.classList.add('active');
      overlay.setAttribute('aria-hidden', 'false');
      body.style.overflow = 'hidden';
      overlay.addEventListener('keydown', handleFocusTrap);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      setTimeout(() => input?.focus({ preventScroll: true }), 50);
    };

    const closeOverlay = () => {
      overlay.classList.remove('active');
      overlay.setAttribute('aria-hidden', 'true');
      body.style.overflow = '';
      overlay.removeEventListener('keydown', handleFocusTrap);
      document.querySelectorAll('[data-search-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      resetPanel();
    };

    const closeMobileMenu = () => {
      if (!mobileMenuOverlay?.classList.contains('active')) return;
      mobileMenuOverlay.classList.remove('active');
      body.style.overflow = '';
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Event Listeners
    // ─────────────────────────────────────────────────────────────────────────────

    toggles.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        closeMobileMenu();
        openOverlay(btn);
      });
    });

    background?.addEventListener('click', closeOverlay);
    closeButton?.addEventListener('click', closeOverlay);

    clearButton?.addEventListener('click', () => {
      if (input) { input.value = ''; input.focus({ preventScroll: true }); }
        resetPanel();
      });

    input?.addEventListener('input', handleInput);
    input?.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('active')) {
        e.preventDefault();
        closeOverlay();
      }
    });

    resetPanel();
  });
})();
